import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { TextField, SelectField, AreaField } from "@/components/common";
import { LEAD_STATUS, PRIORITY, PROPERTY_TYPE, STATUS_COLORS, PRIORITY_COLORS, usd, fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, Sparkles, X, ExternalLink } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { buildPropertyResearchLinks, RESEARCH_SITES, normalizeAddressClient } from "@/lib/research";

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const filtered = leads.filter((l) => {
    const okStatus = statusFilter === "all" || l.status === statusFilter;
    const haystack = [
      l.owner?.first_name, l.owner?.last_name, l.owner?.entity_name,
      l.property?.property_address, l.property?.city, l.lead_id_display,
    ].filter(Boolean).join(" ").toLowerCase();
    const query = q.trim().toLowerCase();
    return okStatus && (!query || haystack.includes(query));
  });

  return (
    <div>
      <PageHeader title="Lead Database" subtitle={q.trim() || statusFilter !== "all" ? `${filtered.length} of ${leads.length} deals match` : `${leads.length} leads in pipeline`}
        action={<NewLeadDialog open={open} setOpen={setOpen} onDone={load} />} />
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search deals by name or company…" className="pl-9 pr-9" data-testid="lead-search" />
            {q && <button onClick={() => setQ("")} data-testid="lead-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-56" data-testid="lead-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{LEAD_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="bg-card border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary">
                <TableHead className="hidden md:table-cell">Lead</TableHead><TableHead>Owner</TableHead><TableHead className="hidden lg:table-cell">Property</TableHead>
                <TableHead>Status</TableHead><TableHead className="hidden sm:table-cell">Priority</TableHead>
                <TableHead className="text-right">Asking</TableHead><TableHead className="hidden lg:table-cell">Next Follow-Up</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((l) => (
                <TableRow key={l.id} className="cursor-pointer hover:bg-secondary/60 transition-colors" onClick={() => nav(`/leads/${l.id}`)} data-testid={`lead-row-${l.id}`}>
                  <TableCell className="hidden md:table-cell font-mono-nums text-xs">{l.lead_id_display}</TableCell>
                  <TableCell className="font-medium">{l.owner?.first_name} {l.owner?.last_name}{l.owner?.entity_name && <span className="block text-xs font-normal text-muted-foreground">{l.owner.entity_name}</span>}<span className="block lg:hidden text-xs font-normal text-muted-foreground truncate max-w-[160px]">{l.property?.property_address}</span></TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground text-sm">{l.property?.property_address}, {l.property?.city}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS_COLORS[l.status] || "bg-slate-100 text-slate-700"}>{l.status}</Badge></TableCell>
                  <TableCell className="hidden sm:table-cell"><Badge variant="outline" className={PRIORITY_COLORS[l.priority]}>{l.priority}</Badge></TableCell>
                  <TableCell className="text-right font-mono-nums">{usd(l.asking_price)}</TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{fmtDate(l.next_follow_up)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" data-testid={`view-links-${l.id}`}><ExternalLink className="w-3.5 h-3.5 mr-1" /> View</Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {RESEARCH_SITES.map(([k, label]) => (
                          <DropdownMenuItem key={k} onClick={() => window.open(buildPropertyResearchLinks(normalizeAddressClient(l.property))[k], "_blank")} data-testid={`view-link-${k}-${l.id}`}>{label}</DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No leads found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function NewLeadDialog({ open, setOpen, onDone }) {
  const [f, setF] = useState({ status: "New", priority: "Normal", property_type: "Single Family", lead_source: "Direct Mail" });
  const [looking, setLooking] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const nav = useNavigate();

  const lookup = async () => {
    if (!f.property_address) return toast.error("Enter a property address first");
    setLooking(true);
    try {
      const addr2 = [f.city, f.state].filter(Boolean).join(", ") + (f.zip_code ? ` ${f.zip_code}` : "");
      const { data } = await api.get("/properties/lookup", { params: { address1: f.property_address, address2: addr2 } });
      const p = data.property;
      const [fn, ...rest] = (p.owner_name || "").split(" ");
      setF((prev) => ({
        ...prev,
        first_name: prev.first_name || fn || "",
        last_name: prev.last_name || rest.join(" ") || "",
        property_address: p.property_address || prev.property_address,
        city: p.city || prev.city,
        state: p.state || prev.state,
        zip_code: p.zip_code || prev.zip_code,
        estimated_market_value: p.estimated_market_value || prev.estimated_market_value,
        property_type: p.property_type || prev.property_type,
        parcel_number: p.parcel_number,
        _attom: p,
      }));
      toast.success(`Record found via ${data.source}${data.county_office ? " · county office matched" : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No record found for that address");
    } finally { setLooking(false); }
  };

  const create = async () => {
    try {
      const owner = (await api.post("/owners", { first_name: f.first_name, last_name: f.last_name, entity_name: f.entity_name, phone: f.phone, email: f.email })).data;
      const a = f._attom || {};
      const prop = (await api.post("/properties", { property_address: f.property_address, city: f.city, state: f.state, zip_code: f.zip_code, county: a.county, parcel_number: f.parcel_number, property_type: f.property_type, estimated_market_value: f.estimated_market_value, bedrooms: a.bedrooms, bathrooms: a.bathrooms, square_feet: a.square_feet, year_built: a.year_built, property_taxes_annual: a.property_taxes_annual, current_owner_id: owner.id })).data;
      const lead = (await api.post("/leads", { owner_id: owner.id, property_id: prop.id, status: f.status, priority: f.priority, lead_source: f.lead_source, asking_price: f.asking_price, seller_motivation: f.seller_motivation, deal_completeness_score: a.estimated_equity != null ? 60 : undefined })).data;
      if (a.estimated_loan_balance) {
        await api.post("/loans", { lead_id: lead.id, property_id: prop.id, lender_name: a.mortgage_lender, original_loan_amount: a.estimated_loan_balance, estimated_current_balance: a.estimated_loan_balance, statement_verified: false });
      }
      toast.success("Lead created");
      setOpen(false);
      onDone();
      nav(`/leads/${lead.id}`);
    } catch { toast.error("Failed to create lead"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-lead-btn"><Plus className="w-4 h-4 mr-1" /> New Lead</Button></DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Create New Lead</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Property Address" value={f.property_address} onChange={set("property_address")} testid="nl-address" className="sm:col-span-2" />
          <div className="sm:col-span-2 -mt-1">
            <Button type="button" variant="outline" size="sm" onClick={lookup} disabled={looking} data-testid="nl-attom-lookup">
              <Sparkles className="w-4 h-4 mr-1" /> {looking ? "Looking up…" : "Auto-fill from county records (ATTOM)"}
            </Button>
            {f.parcel_number && <span className="ml-3 text-xs text-muted-foreground">APN: <span className="font-mono-nums">{f.parcel_number}</span></span>}
          </div>
          {f._attom && (f._attom.avm_value || f._attom.estimated_equity != null) && (
            <div className="sm:col-span-2 grid grid-cols-3 gap-3 rounded-md border border-border bg-secondary/50 p-3" data-testid="nl-attom-summary">
              <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">ATTOM AVM</div><div className="font-display font-bold font-mono-nums">{usd(f._attom.avm_value)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Est. Loan Balance</div><div className="font-display font-bold font-mono-nums">{usd(f._attom.estimated_loan_balance)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Est. Equity</div><div className="font-display font-bold font-mono-nums text-accent">{usd(f._attom.estimated_equity)}</div></div>
            </div>
          )}
          <TextField label="Owner First Name" value={f.first_name} onChange={set("first_name")} testid="nl-first" />
          <TextField label="Owner Last Name" value={f.last_name} onChange={set("last_name")} testid="nl-last" />
          <TextField label="Company / Entity" value={f.entity_name} onChange={set("entity_name")} testid="nl-entity" className="sm:col-span-2" />
          <TextField label="Phone" value={f.phone} onChange={set("phone")} testid="nl-phone" />
          <TextField label="Email" value={f.email} onChange={set("email")} testid="nl-email" />
          <TextField label="City" value={f.city} onChange={set("city")} testid="nl-city" />
          <TextField label="State" value={f.state} onChange={set("state")} testid="nl-state" />
          <TextField label="ZIP" value={f.zip_code} onChange={set("zip_code")} testid="nl-zip" />
          <SelectField label="Property Type" value={f.property_type} onChange={set("property_type")} options={PROPERTY_TYPE} testid="nl-type" />
          <SelectField label="Status" value={f.status} onChange={set("status")} options={LEAD_STATUS} testid="nl-status" />
          <SelectField label="Priority" value={f.priority} onChange={set("priority")} options={PRIORITY} testid="nl-priority" />
          <TextField label="Asking Price ($)" type="number" value={f.asking_price} onChange={set("asking_price")} testid="nl-asking" />
          <TextField label="Est. Market Value ($)" type="number" value={f.estimated_market_value} onChange={set("estimated_market_value")} testid="nl-value" />
          <div className="sm:col-span-2"><AreaField label="Seller Motivation" value={f.seller_motivation} onChange={set("seller_motivation")} testid="nl-motivation" /></div>
        </div>
        <DialogFooter><Button onClick={create} data-testid="nl-submit">Create Lead</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
